import { useTrialStatus } from "@/modules/shared/hooks/useTrialStatus";
import { useAuth } from "@/modules/shared/hooks/useAuth";
import { Button } from "@/modules/shared/components/ui/button";
import { Clock, Lock, Sparkles, Hourglass, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export function TrialExpiredGuard({ children }: { children: React.ReactNode }) {
  const { isExpired, isTrialing, isPendingApproval, isSuspended, isBlocked, daysRemaining, trialEndsAt, subscriptionStatus, loading } = useTrialStatus();
  const { signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ink)]" />
      </div>
    );
  }

  // Block access when pending approval
  if (isPendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center">
            <Hourglass className="h-10 w-10 text-amber-600" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">
              Cadastro em análise
            </h1>
            <p className="text-slate-500">
              Sua empresa foi cadastrada com sucesso! Nossa equipe está analisando sua solicitação e em breve você receberá acesso à plataforma.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[color:var(--line)] p-6 space-y-4">
            <div className="flex items-center gap-3 text-left">
              <Clock className="h-5 w-5 text-amber-500 shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Aprovação pendente</p>
                <p className="text-xs text-slate-500">Você será notificado por e-mail quando sua conta for liberada</p>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            className="text-slate-400 hover:text-slate-600"
            onClick={signOut}
          >
            Sair da conta
          </Button>
        </div>
      </div>
    );
  }

  // Block access when suspended
  if (isSuspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-orange-100 flex items-center justify-center">
            <AlertTriangle className="h-10 w-10 text-orange-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">Conta suspensa</h1>
            <p className="text-slate-500">
              Sua organização foi temporariamente suspensa. Entre em contato com nossa equipe para mais informações e regularização.
            </p>
          </div>
          <Button variant="ghost" className="text-slate-400 hover:text-slate-600" onClick={signOut}>
            Sair da conta
          </Button>
        </div>
      </div>
    );
  }

  // Block access when blocked
  if (isBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center">
            <Lock className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">Conta bloqueada</h1>
            <p className="text-slate-500">
              Sua organização foi bloqueada permanentemente. Se acredita que isso é um erro, entre em contato com o suporte.
            </p>
          </div>
          <Button variant="ghost" className="text-slate-400 hover:text-slate-600" onClick={signOut}>
            Sair da conta
          </Button>
        </div>
      </div>
    );
  }

  // Block access when payment failed (past_due)
  if (subscriptionStatus === "past_due") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-orange-100 flex items-center justify-center">
            <AlertTriangle className="h-10 w-10 text-orange-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">
              Pagamento pendente
            </h1>
            <p className="text-slate-500">
              Houve uma falha no processamento do seu pagamento. Para continuar usando o OpenHub, entre em contato com nossa equipe comercial para regularizar sua assinatura.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[color:var(--line)] p-6 space-y-4">
            <div className="flex items-center gap-3 text-left">
              <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Ação necessária</p>
                <p className="text-xs text-slate-500">Regularize seu pagamento para restaurar o acesso à plataforma</p>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            className="text-slate-400 hover:text-slate-600"
            onClick={signOut}
          >
            Sair da conta
          </Button>
        </div>
      </div>
    );
  }

  // Block access when subscription canceled
  if (subscriptionStatus === "canceled") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center">
            <Lock className="h-10 w-10 text-red-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">
              Assinatura cancelada
            </h1>
            <p className="text-slate-500">
              Sua assinatura do OpenHub foi cancelada. Para reativar o acesso, entre em contato com nossa equipe comercial.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[color:var(--line)] p-6 space-y-4">
            <div className="flex items-center gap-3 text-left">
              <Sparkles className="h-5 w-5 text-[var(--ink)] shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Reativar plano</p>
                <p className="text-xs text-slate-500">Fale com nossa equipe comercial para voltar a utilizar a plataforma</p>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            className="text-slate-400 hover:text-slate-600"
            onClick={signOut}
          >
            Sair da conta
          </Button>
        </div>
      </div>
    );
  }

  // Show trial banner when trialing but not expired
  if (isTrialing && !isExpired && daysRemaining <= 3) {
    return (
      <>
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800 flex items-center justify-center gap-2">
          <Clock className="inline h-4 w-4 -mt-0.5" />
          Seu trial expira em <strong>{daysRemaining} {daysRemaining === 1 ? "dia" : "dias"}</strong>
          {trialEndsAt && (
            <span className="text-amber-600">
              ({format(trialEndsAt, "dd 'de' MMMM", { locale: ptBR })})
            </span>
          )}
          <span className="ml-2 text-xs text-amber-600">
            Entre em contato com nossa equipe comercial para ativar seu plano.
          </span>
        </div>
        {children}
      </>
    );
  }

  // Block access when expired
  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--cream)] p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center">
            <Lock className="h-10 w-10 text-red-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-slate-900">
              Período de teste encerrado
            </h1>
            <p className="text-slate-500">
              Seu trial de 7 dias expirou
              {trialEndsAt && (
                <> em {format(trialEndsAt, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</>
              )}
              . Para continuar usando o OpenHub, entre em contato com nossa equipe comercial.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[color:var(--line)] p-6 space-y-4">
            <div className="flex items-center gap-3 text-left">
              <Sparkles className="h-5 w-5 text-[color:var(--lime)] shrink-0" />
              <div>
                <p className="font-medium text-slate-900 text-sm">Plano completo</p>
                <p className="text-xs text-slate-500">Acesso ilimitado a todas as funcionalidades</p>
              </div>
            </div>

            <p className="text-sm text-slate-500">
              Fale com nossa equipe comercial para ativar seu plano e continuar usando a plataforma.
            </p>
          </div>

          <Button
            variant="ghost"
            className="text-slate-400 hover:text-slate-600"
            onClick={signOut}
          >
            Sair da conta
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
